import { createEffect, createSignal, createUniqueId, onCleanup, onMount } from 'solid-js'

import { animateFlame } from './flameAnimation'

type HabitCompletionBorderProps = {
   flaming: boolean
}

/**
 * Renders the animated border shown when a habit is completed, plus the flame
 * layer and embers used while a habit is overdue.
 *
 * @param props - Whether the overdue flames should animate.
 * @returns The border element.
 */
const HabitCompletionBorder = (props: HabitCompletionBorderProps) => {
   // Each card needs its own filter id; duplicate ids resolve to the first match.
   const flameFilterId = `habit-flame-${createUniqueId()}`
   let border: SVGSVGElement | undefined
   let turbulence: SVGFETurbulenceElement | undefined
   const [nearViewport, setNearViewport] = createSignal(false)

   // Repainting the flame filter while scrolling it into view makes scrolling
   // stutter, so only animate cards that are on (or about to enter) the screen.
   onMount(() => {
      const observer = new IntersectionObserver(
         ([entry]) => setNearViewport(entry?.isIntersecting ?? false),
         { rootMargin: '50px 0px' },
      )
      if (border) {
         observer.observe(border)
      }
      onCleanup(() => observer.disconnect())
   })

   createEffect(() => {
      if (props.flaming && nearViewport() && turbulence) {
         onCleanup(animateFlame(turbulence))
      }
   })

   return (
      <svg ref={border} aria-hidden="true" class="habit-completion-border">
         <defs>
            <filter id={flameFilterId} x="-10%" y="-25%" width="120%" height="150%">
               {/* Vertically stretched noise shapes the wavering flame tongues. */}
               <feTurbulence
                  ref={turbulence}
                  type="fractalNoise"
                  baseFrequency="0.018 0.085"
                  numOctaves="2"
                  seed="7"
                  result="noise"
               />
               <feDisplacementMap
                  in="SourceGraphic"
                  in2="noise"
                  scale="9"
                  xChannelSelector="R"
                  yChannelSelector="G"
               />
               <feGaussianBlur stdDeviation="1.4" />
            </filter>
         </defs>
         <rect class="habit-overdue-flame" filter={`url(#${flameFilterId})`} />
         <rect class="habit-completion-border-glow" pathLength="100" />
         <rect class="habit-completion-border-line" pathLength="100" />
         <circle class="habit-overdue-ember" cx="18%" cy="0" r="1.7" />
         <circle class="habit-overdue-ember" cx="57%" cy="0" r="1.5" />
         <circle class="habit-overdue-ember" cx="84%" cy="0" r="1.8" />
      </svg>
   )
}

export default HabitCompletionBorder
